import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../components/CourierEntryEdit.css";
import { hasPermission } from "../utils/permissions";

const API_BASE = "https://newerp.awasthivitality.com/api";
const COURIER_API = "https://newerp.awasthivitality.com/api/couriers";
const PATIENTS_API = "https://newerp.awasthivitality.com/api/patients";

const CourierEntryEdit = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const deliveryId = params.get("delivery_id") || params.get("id");

  const [patients, setPatients] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [existingFile, setExistingFile] = useState({ path: "", name: "", mime: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);

  const [form, setForm] = useState({
    patient_id: "",
    courier_id: "",
    delivery_date: "",
    contact_name: "",
    contact_mobile: "",
    tracking_no: "",
    tracking_id: "",
    mop: "ONLINE",
    course: "",
    delivery_courceid: "",
    payment_status: "PAID",
    status: "UNDELIVERED",
    delivery_status: "Enabled",
  });

  useEffect(() => {
    (async () => {
      try {
        const pUrl = new URL(PATIENTS_API);
        pUrl.searchParams.set("page", "1");
        pUrl.searchParams.set("per_page", "100");
        const pRes = await fetch(pUrl.toString(), { method: "GET" });
        const pJson = await pRes.json().catch(() => ({}));
        const pdata = pJson?.data || pJson;
        const plist = Array.isArray(pdata?.patients)
          ? pdata.patients
          : Array.isArray(pdata)
          ? pdata
          : Array.isArray(pJson)
          ? pJson
          : [];
        setPatients(plist);
      } catch {
        // ignore preload errors
      }

      // Load couriers identically to CourierEntry.js
      try {
        const cUrl = COURIER_API;
        const resp = await fetch(cUrl, { method: "GET" });
        const json = await resp.json().catch(() => ({}));
        const cdata = json?.data || json;
        const clist = Array.isArray(cdata) ? cdata : [];
        setCouriers(clist);
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    const checkPerm = async () => {
      const canEdit = await hasPermission('delivery', 'editdelivery');
      if (!canEdit) {
        toast.error('You do not have permission to edit courier entries.');
        setTimeout(() => navigate('/CourierEntryList'), 2000);
        setHasAccess(false);
      } else {
        setHasAccess(true);
      }
    };
    checkPerm();
    
    // Listen for permission updates
    const handlePermissionUpdate = () => {
      checkPerm();
    };
    window.addEventListener('permissionsUpdated', handlePermissionUpdate);
    return () => window.removeEventListener('permissionsUpdated', handlePermissionUpdate);
  }, [navigate]);

  useEffect(() => {
    const loadCourses = async () => {
      if (!form.patient_id) {
        setCourses([]);
        setForm((prev) => ({ ...prev, course: "", delivery_courceid: "" }));
        return;
      }
      try {
        const token = (localStorage.getItem("session_token") || "").trim();
        const viewUrl = `https://newerp.awasthivitality.com/api/patient/view/${form.patient_id}${
          token ? `?token=${encodeURIComponent(token)}` : ""
        }`;
        let resp = await fetch(viewUrl, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(token ? { "X-Session-Token": token } : {}),
          },
        });
        if (!resp.ok) {
          resp = await fetch(
            `https://newerp.awasthivitality.com/api/patient/details/${form.patient_id}`
          );
        }
        const json = await resp.json().catch(() => ({}));
        const d = json?.data || {};
        let list = [];
        if (Array.isArray(d.patient_courses)) list = d.patient_courses;
        else if (Array.isArray(d.courses)) list = d.courses;
        else if (Array.isArray(d.course_data)) list = d.course_data;

        const normalized = [];
        (Array.isArray(list) ? list : []).forEach((c) => {
          const primaryId = c.patient_course_id ?? c.id ?? c.course_id;
          const primaryName =
            c.diagnosis || c.diagnosis_name || c.name || c.diagnosis?.name;
          const primaryDiagId =
            c.diagnosis_id ??
            c.diagnosis?.diagnosis_id ??
            c.diagnosisId ??
            c.diagnosis_code_id;
          if (primaryId && (primaryName || primaryDiagId)) {
            normalized.push({
              patient_course_id: String(primaryId),
              diagnosis_id: primaryDiagId ? String(primaryDiagId) : "",
              diagnosis_label: primaryName || "",
            });
          }
          const secondaryId = c.patient_course_id1;
          const secondaryName = c.diagnosis1;
          const secondaryDiagId = c.diagnosis_id1;
          if (secondaryId && (secondaryName || secondaryDiagId)) {
            normalized.push({
              patient_course_id: String(secondaryId),
              diagnosis_id: secondaryDiagId ? String(secondaryDiagId) : "",
              diagnosis_label: secondaryName || "",
            });
          }
        });
        setCourses(normalized);

        if (
          !normalized.some(
            (c) => String(c.patient_course_id) === String(form.delivery_courceid)
          )
        ) {
          setForm((prev) => ({ ...prev, course: "", delivery_courceid: "" }));
        }
      } catch {
        setCourses([]);
        setForm((prev) => ({ ...prev, course: "", delivery_courceid: "" }));
      }
    };
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.patient_id]);

  useEffect(() => {
    if (!deliveryId) return;
    (async () => {
      setLoading(true);
      try {
        const token = (localStorage.getItem("session_token") || "").trim();

        const candidates = [
          `${API_BASE}/deliveries/${deliveryId}`,
          `${API_BASE}/delivery/${deliveryId}`,
          `${API_BASE}/delivery/view/${deliveryId}`,
          `${API_BASE}/courier-entry/${deliveryId}`,
          `${API_BASE}/deliveries?delivery_id=${encodeURIComponent(deliveryId)}`,
        ];

        const fetchFlexible = async (target) => {
          const u = new URL(target);
          if (token) u.searchParams.set("session_token", token);
          const resp = await fetch(u.toString(), { method: "GET" });
          let data = null;
          try {
            data = await resp.json();
          } catch {
            data = null;
          }
          return { resp, data };
        };

        let found = null;
        for (const c of candidates) {
          try {
            const { resp, data } = await fetchFlexible(c);
            if (!data) continue;
            // Accept various shapes
            const payload = Array.isArray(data?.data)
              ? data.data[0]
              : Array.isArray(data)
              ? data[0]
              : data?.data || data;
            if (payload && (resp.ok || payload.delivery_id || payload.patient_id)) {
              found = payload;
              break;
            }
          } catch {
            // try next
          }
        }

        if (!found) {
          throw new Error("Delivery not found or API unavailable");
        }

        setForm({
          patient_id: found.patient_id ? String(found.patient_id) : "",
          courier_id: found.courier_id ? String(found.courier_id) : "",
          delivery_date: found.delivery_date || "",
          contact_name: found.contact_name || "",
          contact_mobile: found.contact_mobile || "",
          tracking_no: found.tracking_no || "",
          tracking_id: found.tracking_id || "",
          mop: found.mop || "ONLINE",
          course: found.course ? String(found.course) : "",
          delivery_courceid: found.delivery_courceid ? String(found.delivery_courceid) : "",
          payment_status: found.payment_status || "PAID",
          status: found.status || "UNDELIVERED",
          delivery_status: found.delivery_status || "Enabled",
        });
        setExistingFile({
          path: (found.invoice || found.pdf_file || ""),
          name: found.name || "",
          mime: found.mime || "",
        });
      } catch (e) {
        toast.error(
          (e && e.message) || "❌ Failed to load delivery. Please try again."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [deliveryId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCourseSelect = (e) => {
    const selectedId = e.target.value;
    const selected = courses.find(
      (c) => String(c.patient_course_id) === String(selectedId)
    );
    setForm({
      ...form,
      course: selected?.diagnosis_id ? String(selected.diagnosis_id) : "",
      delivery_courceid: selected ? String(selected.patient_course_id) : "",
    });
  };

  const handleFile = (e) => {
    setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!deliveryId) {
      toast.error("Missing delivery id");
      return;
    }
    const {
      patient_id,
      courier_id,
      delivery_date,
      contact_name,
      contact_mobile,
      tracking_no,
      tracking_id,
      course,
      delivery_courceid,
      mop,
      payment_status,
      status,
      delivery_status,
    } = form;
    if (
      !patient_id ||
      !courier_id ||
      !delivery_date ||
      !contact_name ||
      !contact_mobile ||
      !tracking_no ||
      !delivery_courceid
    ) {
      toast.warn("Please fill all required fields.");
      return;
    }

    // Build both multipart (for file) and urlencoded (no file) bodies
    const fd = new FormData();
    const up = new URLSearchParams();

    const assignField = (key, value) => {
      if (value === undefined || value === null) return;
      const v = String(value);
      fd.append(key, v);
      up.set(key, v);
    };

    assignField("patient_id", patient_id);
    if (courier_id) assignField("courier_id", courier_id);
    assignField("delivery_date", delivery_date);
    assignField("contact_name", contact_name);
    assignField("contact_mobile", contact_mobile);
    assignField("tracking_no", tracking_no);
    assignField("mop", mop || "ONLINE");
    if (payment_status) assignField("payment_status", payment_status);
    if (status) assignField("status", status);
    if (delivery_status) assignField("delivery_status", delivery_status);
    if (course) assignField("course", course);
    if (delivery_courceid) assignField("delivery_courceid", delivery_courceid);
    assignField("location", "");
    assignField("code", "");
    assignField("remark", "");
    if (tracking_id) assignField("tracking_id", tracking_id);
    // Include file (send under multiple common keys for backend compatibility)
    if (file) {
      // Sanitize filename to avoid DB collation issues
      const toAscii = (str) => {
        try {
          return (str || "")
            .normalize("NFKD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, " ")
            .replace(/[^\x00-\x7F]/g, "");
        } catch {
          return (str || "").replace(/[^\x00-\x7F]/g, "");
        }
      };
      const sanitizeFileName = (name) => {
        const ascii = toAscii(name);
        const lastDot = ascii.lastIndexOf(".");
        const base = (lastDot > 0 ? ascii.slice(0, lastDot) : ascii) || "file";
        const ext = lastDot > 0 ? ascii.slice(lastDot + 1) : "";
        const cleanBase = base
          .replace(/[^A-Za-z0-9._ -]+/g, "_")
          .replace(/[\s]+/g, " ")
          .trim()
          .replace(/[\s]+/g, "_")
          .replace(/_+/g, "_");
        const cleanExt = ext.replace(/[^A-Za-z0-9]+/g, "");
        return cleanExt ? `${cleanBase}.${cleanExt}` : cleanBase;
      };
      const safeName = sanitizeFileName(file.name || "upload");
      const safeFile = new File([file], safeName, { type: file.type || "application/octet-stream" });
      // Send only the expected key
      fd.append("invoice", safeFile);
      // Ensure backend stores metadata consistently
      assignField("name", safeName);
      assignField("mime", file.type || "");
    } else if (existingFile?.path || existingFile?.name) {
      // Preserve existing file metadata so backend doesn't null it
      const parts = String(existingFile.path || "").split("/");
      const baseName = existingFile.name || parts[parts.length - 1] || "";
      if (baseName) assignField("name", baseName);
      if (existingFile?.mime) assignField("mime", existingFile.mime);
    }

    // Also include legacy/admin expected field names for maximum compatibility
    if (courier_id) assignField("courier", courier_id);
    assignField("courier_date", delivery_date);
    assignField("submit", "Update");
    assignField("delivery_id", String(deliveryId));

    try {
      const token = (localStorage.getItem("session_token") || "").trim();
      setSaving(true);

      const tryRequests = async () => {
        let resp;
        if (file) {
          // Many servers block multipart PUT; prefer POST override first when uploading
          const uA = new URL(`${API_BASE}/update-deliveries/${deliveryId}`);
          if (token) uA.searchParams.set("session_token", token);
          uA.searchParams.set("_method", "PUT");
          resp = await fetch(uA.toString(), { method: "POST", body: fd });
          if (resp.ok) return resp;

          const uB = new URL(`${API_BASE}/deliveries/${deliveryId}`);
          if (token) uB.searchParams.set("session_token", token);
          uB.searchParams.set("_method", "PUT");
          resp = await fetch(uB.toString(), { method: "POST", body: fd });
          if (resp.ok) return resp;

          const uC = new URL(`${API_BASE}/update-deliveries/${deliveryId}`);
          if (token) uC.searchParams.set("session_token", token);
          resp = await fetch(uC.toString(), { method: "PUT", body: fd });
          if (resp.ok) return resp;

          const uD = new URL(`${API_BASE}/deliveries/${deliveryId}`);
          if (token) uD.searchParams.set("session_token", token);
          resp = await fetch(uD.toString(), { method: "PUT", body: fd });
          return resp;
        } else {
          // No file: URL-encoded is fine
          const u1 = new URL(`${API_BASE}/update-deliveries/${deliveryId}`);
          if (token) u1.searchParams.set("session_token", token);
          resp = await fetch(u1.toString(), { method: "PUT", body: up, headers: { "Content-Type": "application/x-www-form-urlencoded" } });
          if (resp.ok) return resp;

          const u2 = new URL(`${API_BASE}/update-deliveries/${deliveryId}`);
          if (token) u2.searchParams.set("session_token", token);
          u2.searchParams.set("_method", "PUT");
          resp = await fetch(u2.toString(), { method: "POST", body: up, headers: { "Content-Type": "application/x-www-form-urlencoded" } });
          if (resp.ok) return resp;

          const u3 = new URL(`${API_BASE}/deliveries/${deliveryId}`);
          if (token) u3.searchParams.set("session_token", token);
          resp = await fetch(u3.toString(), { method: "PUT", body: up, headers: { "Content-Type": "application/x-www-form-urlencoded" } });
          if (resp.ok) return resp;

          const u4 = new URL(`${API_BASE}/deliveries/${deliveryId}`);
          if (token) u4.searchParams.set("session_token", token);
          u4.searchParams.set("_method", "PUT");
          resp = await fetch(u4.toString(), { method: "POST", body: up, headers: { "Content-Type": "application/x-www-form-urlencoded" } });
          return resp;
        }
      };

      const response = await tryRequests();
      const res = await response.json().catch(() => ({}));
      if (response.ok && res && res.success !== false) {
        // Read-after-write: fetch latest and update local state to avoid false mismatch warnings
        try {
          const verifyUrl = new URL(`${API_BASE}/deliveries/${deliveryId}`);
          if (token) verifyUrl.searchParams.set("session_token", token);
          verifyUrl.searchParams.set("_ts", String(Date.now()));
          const verifyResp = await fetch(verifyUrl.toString(), { method: 'GET', cache: 'no-store' });
          const verifyJson = await verifyResp.json().catch(() => ({}));
          const d = verifyJson?.data || verifyJson;
          if (verifyResp.ok && d) {
            setForm({
              patient_id: d.patient_id ? String(d.patient_id) : "",
              courier_id: d.courier_id ? String(d.courier_id) : "",
              delivery_date: d.delivery_date || "",
              contact_name: d.contact_name || "",
              contact_mobile: d.contact_mobile || "",
              tracking_no: d.tracking_no || "",
              tracking_id: d.tracking_id || "",
              mop: d.mop || "ONLINE",
              course: d.course ? String(d.course) : "",
              delivery_courceid: d.delivery_courceid ? String(d.delivery_courceid) : "",
              payment_status: d.payment_status || "PAID",
              status: d.status || "UNDELIVERED",
              delivery_status: d.delivery_status || "Enabled",
            });
            setExistingFile({ path: (d.invoice || d.pdf_file || ""), name: d.name || "", mime: d.mime || "" });
          }
        } catch {}
        toast.success(res.msg || res.message || 'Updated successfully');
        navigate('/CourierEntryList');
      } else {
        const firstError =
          res?.errors && typeof res.errors === "object"
            ? Object.values(res.errors).flat()[0]
            : null;
        toast.error(
          firstError || res?.message || res?.msg || `Update failed (HTTP ${response.status})`
        );
      }
    } catch (err) {
      toast.error(
        "❌ Network Error. Please check your connection and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="courier-edit-container">
        <div className="courier-edit-card">
          <div style={{ padding: 20, textAlign: 'center' }}>
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="courier-edit-container">
      <div className="courier-edit-card">
        <h2 className="courier-edit-title">Courier Edit {loading ? "(Loading...)" : ""}</h2>
        <form className="courier-edit-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>
                Courier Date <span className="required">*</span>
              </label>
              <input
                type="date"
                name="delivery_date"
                value={form.delivery_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>
                Contact Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="contact_name"
                value={form.contact_name}
                onChange={handleChange}
                placeholder="Enter contact name"
                required
              />
            </div>

            <div className="form-group">
              <label>
                Courier <span className="required">*</span>
              </label>
              <select
                name="courier_id"
                value={form.courier_id}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {couriers.map((c) => (
                  <option key={c.courier_id} value={c.courier_id}>
                    {c.courier_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>
                Contact Mobile <span className="required">*</span>
              </label>
              <input
                type="tel"
                name="contact_mobile"
                value={form.contact_mobile}
                onChange={handleChange}
                placeholder="Enter mobile number"
                required
              />
            </div>

            <div className="form-group">
              <label>
                Mode of Payment <span className="required">*</span>
              </label>
              <select
                name="mop"
                value={form.mop}
                onChange={handleChange}
                required
              >
                <option value="ONLINE">ONLINE</option>
                <option value="COD">COD</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                Patient <span className="required">*</span>
              </label>
              <select
                name="patient_id"
                value={form.patient_id}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {patients.map((p) => (
                  <option key={p.patient_id} value={p.patient_id}>
                    {p.pr_no || p.patient_id} / {p.name} / {p.mobile}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>
                Course Select <span className="required">*</span>
              </label>
              <select
                name="delivery_courceid"
                value={form.delivery_courceid}
                onChange={handleCourseSelect}
                required
              >
                <option value="">Select</option>
                {courses.map((c) => (
                  <option key={c.patient_course_id} value={c.patient_course_id}>
                    {c.diagnosis_label || `Course ${c.patient_course_id}`}
                  </option>
                ))}
                {/* Fallback to show current selection if not present in loaded list */}
                {!courses.some((c) => String(c.patient_course_id) === String(form.delivery_courceid)) &&
                  form.delivery_courceid && (
                    <option value={form.delivery_courceid}>
                      {`Course ${form.delivery_courceid}`}
                    </option>
                  )}
              </select>
            </div>


            <div className="form-group">
              <label>
                Tracking ID <span className="required">*</span>
              </label>
              <input
                type="text"
                name="tracking_no"
                value={form.tracking_no}
                onChange={handleChange}
                placeholder="Enter tracking ID"
                required
              />
            </div>
{/* 
            <div className="form-group">
              <label>Alt Tracking (optional)</label>
              <input
                type="text"
                name="tracking_id"
                value={form.tracking_id}
                onChange={handleChange}
                placeholder="Enter alt tracking"
              />
            </div> */}

            <div className="form-group full-width">
              <label>Upload PDF</label>
              <input
                type="file"
                name="invoice"
                accept="application/pdf,image/*"
                onChange={handleFile}
              />
              {(existingFile?.path || existingFile?.name) && (
                <div style={{ marginTop: 8 }}>
                  {(() => {
                    const base = API_BASE.replace("/api", "");
                    const relPath = String(existingFile.path || "").replace(/^\/+/, "");
                    const fallbackName = existingFile.name ? existingFile.name : "";
                    // Prefer base/relativePath; fallback to uploads/delivery/{name}
                    let candidate = relPath
                      ? `${base}/${relPath}`
                      : (fallbackName ? `${base}/uploads/delivery/${fallbackName}` : "");
                    if (!candidate) return null;
                    // Safely encode URL (preserve slashes)
                    try { candidate = encodeURI(candidate); } catch {}
                    if ((existingFile.mime || "").startsWith("application/pdf")) {
                      return (
                        <embed src={candidate} type="application/pdf" width="100%" height="400px" />
                      );
                    }
                    if ((existingFile.mime || "").startsWith("image/")) {
                      return (
                        <img src={candidate} alt="invoice" style={{ maxWidth: "100%", maxHeight: 300 }} />
                      );
                    }
                    return (
                      <a href={candidate} target="_blank" rel="noreferrer">
                        View existing file
                      </a>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="update-btn" disabled={saving}>
              {saving ? "Updating..." : "Update"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourierEntryEdit;
